/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import FormGroup from "@mui/material/FormGroup"
import InputLabel from "@mui/material/InputLabel"
import { useEffect, useState } from "react"
import { selectCurrentUser } from "../../../../redux/ducks/AccessControl/slice"
import { useAppSelector } from "../../../../redux/hooks"
import { MERGE_MODES } from "../../../pages/CoverageRequirements/ItemDetails/constants"
import ArgosCheckbox from "../../Form/Checkbox/ArgosCheckbox"
import ArgosSelect, { userSelectOptions } from "../../Form/Select/ArgosSelect"
import ArgosTextField from "../../Form/TextField/ArgosTextField"


interface IMergeSettings {
  Capability: boolean
  Modality: boolean
  DataFlow: boolean
  Safety: boolean
  noStrategy: boolean
}

const defaultMergeSettings: IMergeSettings = {
  Capability: true,
  Modality: true,
  DataFlow: true,
  Safety: false,
  noStrategy: false
}

interface EditMergeSetDetailsProps {
  action: MERGE_MODES
  mergeSetData: any
  userData: any
  onChange: (data: any) => void
}


const EditMergeSetDetails = (props: EditMergeSetDetailsProps) => {
  const fieldWidth = 400
  const fieldHeight = 45

  const gmid = useAppSelector(state => selectCurrentUser(state).id)
  const [mergeSetName, setMergeSetName] = useState<string>("")
  const [mergeSetDescription, setMergeSetDescription] = useState<string>("")
  const [mergeSetOwner, setMergeSetOwner] = useState<string>(gmid)
  const [mergeSettings, setMergeSettings] = useState<IMergeSettings>(defaultMergeSettings)
  const [mergeCapability, setMergeCapability] = useState<boolean>(defaultMergeSettings.Capability)
  const [mergeModality, setMergeModality] = useState<boolean>(defaultMergeSettings.Modality)
  const [mergeDataflow, setMergeDataflow] = useState<boolean>(defaultMergeSettings.DataFlow)
  const [mergeSafety, setMergeSafety] = useState<boolean>(defaultMergeSettings.Safety)
  const [mergeNoStrategy, setMergeNoStrategy] = useState<boolean>(defaultMergeSettings.noStrategy)

  useEffect(() => {
    setMergeSetName(props?.mergeSetData?.name)
    setMergeSetDescription(props?.mergeSetData?.description)
    setMergeSetOwner(props?.mergeSetData?.owner)
    
    setMergeCapability(props?.mergeSetData?.mergeSettings?.Capability)
    setMergeModality(props?.mergeSetData?.mergeSettings?.Modality)
    setMergeDataflow(props?.mergeSetData?.mergeSettings?.DataFlow)
    setMergeSafety(props?.mergeSetData?.mergeSettings?.Safety)
    setMergeNoStrategy(props?.mergeSetData?.mergeSettings?.noStrategy)
  }, [props])

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMergeSetName(event.target.value)
    props.onChange({
      ...props?.mergeSetData,
      "name": event.target.value
    })
  }

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMergeSetDescription(event.target.value)
    props.onChange({
      ...props?.mergeSetData,
      "description": event.target.value
    })
  }
  
  const handleOwnerChange = (event: any) => {
    setMergeSetOwner(event.target.value)
    props.onChange({
      ...props?.mergeSetData,
      "owner": event.target.value
    })
  }

  const handleMergeSettingChange = (event: React.ChangeEvent<HTMLInputElement>, key: string, setStateFunc: any) => {
    setStateFunc(event.target.checked)
    props.onChange({
      ...props?.mergeSetData,
      "mergeSettings": {
        ...props?.mergeSetData?.mergeSettings,
        [key]: event.target.checked
      }
    })
  }

  useEffect(() => {
    setMergeSettings({
      Capability: mergeCapability,
      Modality: mergeModality,
      DataFlow: mergeDataflow,
      Safety: mergeSafety,
      noStrategy: mergeNoStrategy
    })
  }, [mergeCapability, mergeModality, mergeDataflow, mergeSafety, mergeNoStrategy])
  
  return (
    <div style={{padding: 5, alignSelf: 'center', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', alignItems: 'flex-start', gap: 30, height: 480}}>
      <ArgosTextField 
        required
        onChange={handleNameChange}
        value={mergeSetName}
        name="merge_set_name"
        sx={{width: fieldWidth, height: fieldHeight}}
        type="text"
        id="outlined-basic" 
        label="Name"
      />
      <ArgosTextField
        required
        onChange={handleDescriptionChange}
        value={mergeSetDescription}
        name="merge_set_description"
        sx={{width: fieldWidth, height: fieldHeight}}
        type="text"
        id="outlined-basic" 
        label="Description" 
      />
      <FormControl variant="standard" sx={{ width: fieldWidth, height: fieldHeight }}>
        <InputLabel id="merge_set_owner">Owner</InputLabel>
        <ArgosSelect
          required
          label="Owner"
          type="text"
          value={mergeSetOwner}
          id="merge_set_owner"
          onChange={(e) => handleOwnerChange(e)}
          sx={{width: fieldWidth, height: fieldHeight}}
        >
          {userSelectOptions(props.userData)}
        </ArgosSelect>
      </FormControl>
      <FormGroup>
        <FormControlLabel control={<ArgosCheckbox checked={mergeCapability} onChange={(e) => handleMergeSettingChange(e, "Capability", setMergeCapability)}/>} label="Merge on Capability" />
        <FormControlLabel control={<ArgosCheckbox checked={mergeModality} onChange={(e) => handleMergeSettingChange(e, "Modality", setMergeModality)}/>} label="Merge on Modality" />
        <FormControlLabel control={<ArgosCheckbox checked={mergeDataflow} onChange={(e) => handleMergeSettingChange(e, "DataFlow", setMergeDataflow)}/>} label="Merge on Perception Data Flow" />
        <FormControlLabel control={<ArgosCheckbox checked={mergeSafety} onChange={(e) => handleMergeSettingChange(e, "Safety", setMergeSafety)}/>} label="Merge on Safety" />
        <FormControlLabel control={<ArgosCheckbox checked={mergeNoStrategy} onChange={(e) => handleMergeSettingChange(e,  "noStrategy", setMergeNoStrategy)}/>} label="Merge without strategy" />
      </FormGroup>
    </div>
  )
}

export default EditMergeSetDetails
